import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { TrendingUp } from 'lucide-react'
import { mensajeError, obtener } from '../api/client'
import { alerta } from '../utils/alertas'
import type { Inventario, Pedido } from '../types'

const numero = (valor: number) =>
  new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(valor)

const PALETA = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6']

function Tarjeta({
  titulo,
  valor,
  nota,
  acento,
}: {
  titulo: string
  valor: string
  nota?: string
  acento?: 'ok' | 'malo'
}) {
  const color =
    acento === 'ok'
      ? 'text-emerald-600 dark:text-emerald-400'
      : acento === 'malo'
        ? 'text-red-600 dark:text-red-400'
        : 'text-accent'
  return (
    <div className="tarjeta anima-crecer">
      <div className="texto-suave">{titulo}</div>
      <div className={`mt-1.5 text-2xl font-extrabold ${color}`}>{valor}</div>
      {nota && <div className="texto-suave mt-1 text-[0.78rem]">{nota}</div>}
    </div>
  )
}

function Panel({ titulo, ayuda, children }: { titulo: string; ayuda?: string; children: React.ReactNode }) {
  return (
    <div className="tarjeta">
      <div className="fila-entre mb-3 flex-wrap">
        <strong>{titulo}</strong>
        {ayuda && <span className="texto-suave text-[0.78rem]">{ayuda}</span>}
      </div>
      {children}
    </div>
  )
}

export function Analiticas() {
  const [inventario, setInventario] = useState<Inventario[]>([])
  const [pedidos, setPedidos] = useState<Pedido[]>([])
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [inv, ped] = await Promise.all([
        obtener<Inventario[]>('inventario'),
        obtener<Pedido[]>('pedidos'),
      ])
      setInventario(inv ?? [])
      setPedidos(ped ?? [])
    } catch (error) {
      await alerta.error('No se pudieron cargar las analiticas', mensajeError(error))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const totalSkus = useMemo(() => new Set(inventario.map((i) => i.sku)).size, [inventario])
  const totalDisponible = useMemo(
    () => inventario.reduce((sum, i) => sum + (i.disponible ?? 0), 0),
    [inventario]
  )
  const pedidosPendientes = useMemo(
    () => pedidos.filter((p) => !['ENTREGADO', 'CANCELADO'].includes(p.estado?.toUpperCase() ?? '')).length,
    [pedidos]
  )
  const bajoStock = useMemo(
    () => inventario.filter((i) => (i.disponible ?? 0) < 10).length,
    [inventario]
  )

  const porAlmacen = useMemo(() => {
    const mapa = new Map<string, number>()
    for (const i of inventario) {
      const acum = mapa.get(i.almacenNombre) ?? 0
      mapa.set(i.almacenNombre, acum + (i.disponible ?? 0))
    }
    return Array.from(mapa.entries()).map(([nombre, disponible]) => ({ nombre, disponible }))
  }, [inventario])

  const porEstado = useMemo(() => {
    const mapa = new Map<string, number>()
    for (const p of pedidos) {
      const k = p.estado || 'SIN_ESTADO'
      mapa.set(k, (mapa.get(k) ?? 0) + 1)
    }
    return Array.from(mapa.entries()).map(([estado, cantidad]) => ({ estado, cantidad }))
  }, [pedidos])

  const topDisponibles = useMemo(
    () =>
      [...inventario]
        .sort((a, b) => (b.disponible ?? 0) - (a.disponible ?? 0))
        .slice(0, 10)
        .map((i) => ({ producto: i.productoNombre, disponible: i.disponible })),
    [inventario]
  )

  return (
    <div className="anima-entrada space-y-5 overflow-auto p-1 max-h-[86vh]">
      <div className="fila-entre flex-wrap">
        <div>
          <h2 className="mt-0 mb-1 text-2xl font-bold">Analiticas</h2>
          <p className="texto-suave mb-0">Resumen operativo de inventarios y pedidos.</p>
        </div>
        <button
          className="btn btn-secundario mr-10"
          onClick={() => void cargar()}
          disabled={cargando}
        >
          <TrendingUp size={16} className={cargando ? 'anima-pulso' : ''} />
          Actualizar
        </button>
      </div>

      {cargando ? (
        <div className="p-12 text-center text-texto-suave">Cargando...</div>
      ) : (
        <>
          <div className="grid-4">
            <Tarjeta titulo="SKU unicos" valor={numero(totalSkus)} nota="Productos en inventario" />
            <Tarjeta titulo="Disponible total" valor={numero(totalDisponible)} />
            <Tarjeta titulo="Pedidos pendientes" valor={numero(pedidosPendientes)} acento={pedidosPendientes > 0 ? 'ok' : undefined} />
            <Tarjeta titulo="Productos bajo stock" valor={numero(bajoStock)} acento={bajoStock > 0 ? 'malo' : undefined} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel titulo="Inventario por almacen" ayuda="Unidades disponibles">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={porAlmacen} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--borde)" />
                    <XAxis dataKey="nombre" tick={{ fill: 'var(--texto-suave)', fontSize: 12 }} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--texto-suave)', fontSize: 12 }} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--superficie)',
                        border: '1px solid var(--borde)',
                        color: 'var(--texto)',
                      }}
                    />
                    <Bar dataKey="disponible" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel titulo="Pedidos por estado" ayuda="Distribucion actual">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={porEstado}
                      dataKey="cantidad"
                      nameKey="estado"
                      innerRadius="52%"
                      outerRadius="80%"
                      paddingAngle={2}
                    >
                      {porEstado.map((_, i) => (
                        <Cell key={i} fill={PALETA[i % PALETA.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'var(--superficie)',
                        border: '1px solid var(--borde)',
                        color: 'var(--texto)',
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel titulo="Top 10 productos con mayor existencia" ayuda="Ordenados por disponible">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topDisponibles}
                    layout="vertical"
                    margin={{ top: 5, right: 40, bottom: 0, left: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--borde)" horizontal={false} />
                    <XAxis type="number" tick={{ fill: 'var(--texto-suave)', fontSize: 12 }} tickLine={false} />
                    <YAxis type="category" dataKey="producto" tick={{ fill: 'var(--texto-suave)', fontSize: 12 }} tickLine={false} width={140} />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--superficie)',
                        border: '1px solid var(--borde)',
                        color: 'var(--texto)',
                      }}
                    />
                    <Bar dataKey="disponible" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel titulo="Productos con bajo stock" ayuda="Menos de 10 unidades disponibles">
              {bajoStock === 0 ? (
                <div className="p-8 text-center text-texto-suave">Sin productos con bajo stock.</div>
              ) : (
                <div className="tabla-contenedor max-h-80">
                  <table>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th className="text-right">Disponible</th>
                        <th className="text-right">Almacen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventario
                        .filter((i) => (i.disponible ?? 0) < 10)
                        .slice(0, 20)
                        .map((i) => (
                          <tr key={i.id}>
                            <td>
                              <div className="font-semibold">{i.productoNombre}</div>
                              <div className="texto-suave text-[0.75rem]">{i.sku}</div>
                            </td>
                            <td className="text-right">{numero(i.disponible)}</td>
                            <td className="text-right">{i.almacenNombre}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <Panel titulo="Pedidos pendientes" ayuda="Estados diferentes a entregado o cancelado">
            {pedidosPendientes === 0 ? (
              <div className="p-8 text-center text-texto-suave">No hay pedidos pendientes.</div>
            ) : (
              <div className="tabla-contenedor max-h-80">
                <table>
                  <thead>
                    <tr>
                      <th>Folio</th>
                      <th>Cliente</th>
                      <th className="text-right">Prioridad</th>
                      <th className="text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos
                      .filter((p) => !['ENTREGADO', 'CANCELADO'].includes(p.estado?.toUpperCase() ?? ''))
                      .slice(0, 20)
                      .map((p) => (
                        <tr key={p.id}>
                          <td>{p.folio}</td>
                          <td>{p.clienteNombre}</td>
                          <td className="text-right">{p.prioridad}</td>
                          <td className="text-right">{p.estado}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  )
}
